import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import apiService from '../services/apiService';
import { isQaEnvironment } from '../utils/subdomain';
import './SyncAmbientes.css';

/**
 * Sincronizador de Ambientes — passo 2: o diff por entidade.
 *
 * Fase 1 é SÓ LEITURA. Não há seleção nem gravação aqui ainda; o objetivo é enxergar a
 * diferença, que hoje não existe em lugar nenhum — nem manualmente.
 *
 * Duas coisas que a tela precisa comunicar e que não são óbvias:
 *
 *  - **Contagem igual não é conteúdo igual.** madeiramadeira tem `prompts` 14 × 14,
 *    `questions` 76 × 76 e `prompt_assignments` 13 × 13 entre PROD e HMG, com texto
 *    diferente nas três. Por isso a coluna que decide é a de conteúdo (fingerprint), não
 *    a de número.
 *  - **O corte.** Produção recebe atendimento enquanto você olha a tela. O corte é fixado
 *    uma vez e usado nos dois lados, senão o número que você aprova não é o que vai.
 */

const NOME = { prod: 'Produção', qa: 'QA', hmg: 'Homologação' };

const DIRECOES = {
    'prod-qa':  { origem: 'prod', destino: 'qa',   classe: 'atendimentos' },
    'prod-hmg': { origem: 'prod', destino: 'hmg',  classe: 'atendimentos' },
    // Mesmo par de ambientes, outra faixa: alinhar HMG com produção. Existe
    // porque QA acumula dado de teste, então `qa-hmg` não serve para isso.
    'prod-hmg-config': { origem: 'prod', destino: 'hmg', classe: 'config' },
    'qa-hmg':   { origem: 'qa',   destino: 'hmg',  classe: 'config' },
    'prod-qa-config': { origem: 'prod', destino: 'qa', classe: 'config' },
    'hmg-qa-config':  { origem: 'hmg',  destino: 'qa', classe: 'config' },
    'qa-prod':  { origem: 'qa',   destino: 'prod', classe: 'config' }
};

export default function SyncDiff({ user }) {
    const { direcao: idDirecao, tenant } = useParams();
    const ehQa = isQaEnvironment();
    const dir = DIRECOES[idDirecao];

    const [dados, setDados] = useState(null);
    const [erro, setErro] = useState(null);
    const [carregando, setCarregando] = useState(true);

    const carregar = useCallback(async (corte) => {
        setCarregando(true);
        setErro(null);
        try {
            const d = await apiService.syncDiff({
                origem: dir.origem, destino: dir.destino,
                subdomain: tenant, classe: dir.classe, corte: corte || null
            });
            setDados(d);
        } catch (e) {
            setErro(e?.response?.data?.error || e.message);
        } finally {
            setCarregando(false);
        }
    }, [dir, tenant]);

    useEffect(() => {
        if (!ehQa || !user?.can_sync || !dir) { setCarregando(false); return; }
        carregar(null);
    }, [ehQa, user, dir, carregar]);

    if (!ehQa || !user?.can_sync) {
        return (
            <div className="sync-wrap">
                <div className="sync-bloqueio">
                    <h1>Sincronizador de Ambientes</h1>
                    <p>Acesso restrito.</p>
                </div>
            </div>
        );
    }

    if (!dir) {
        return (
            <div className="sync-wrap">
                <div className="sync-erro">
                    Direção desconhecida: <code>{idDirecao}</code>.{' '}
                    <a href="/sync">Voltar ao início</a>
                </div>
            </div>
        );
    }

    const comDiferenca = (dados?.entidades || []).filter((e) => !e.iguais && !e.indisponivel);
    const iguais = (dados?.entidades || []).filter((e) => e.iguais);
    const indisponiveis = (dados?.entidades || []).filter((e) => e.indisponivel);

    return (
        <div className="sync-wrap">
            <header className="sync-head">
                <a className="sync-voltar" href="/sync">← Trocar direção ou cliente</a>
                <h1>
                    {NOME[dir.origem]} <span className="sync-seta">→</span> {NOME[dir.destino]}
                    <span className="sync-tenant">{tenant}</span>
                </h1>
                <p className="sync-sub">
                    {dir.classe === 'config' ? 'Configurações' : 'Atendimentos'} ·
                    {' '}somente leitura — nada é gravado nesta tela
                </p>
                {dados?.corte && (
                    <p className="sync-corte">
                        Corte fixado em <strong>{dados.corte.replace('T', ' ').slice(0, 19)}</strong>
                        {' '}· usado nos dois ambientes
                        <button type="button" className="sync-botao-linha"
                                onClick={() => carregar(null)}>recalcular agora</button>
                    </p>
                )}
            </header>

            {erro && <div className="sync-erro">⚠️ {erro}</div>}
            {carregando && <div className="sync-info">Comparando os dois ambientes…</div>}

            {!carregando && dados && (
                <>
                    <div className="sync-resumo">
                        <span className="sync-chip dif">{comDiferenca.length} com diferença</span>
                        <span className="sync-chip igual">{iguais.length} idênticas</span>
                        {indisponiveis.length > 0 && (
                            <span className="sync-chip indisp">
                                {indisponiveis.length} indisponíveis
                            </span>
                        )}
                    </div>

                    <div className="sync-tabela-scroll">
                        <table className="sync-tabela">
                            <thead>
                                <tr>
                                    <th>Entidade</th>
                                    <th className="num">{NOME[dir.origem]}</th>
                                    <th className="num">{NOME[dir.destino]}</th>
                                    <th className="num">Δ</th>
                                    <th>Conteúdo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(dados.entidades || []).map((e) => (
                                    <tr key={e.entidade}
                                        className={e.indisponivel ? 'linha-indisp'
                                                  : (e.iguais ? 'linha-igual' : 'linha-dif')}>
                                        <td>
                                            {e.iguais || e.indisponivel || e.executavel === false ? e.label : (
                                                <a className="sync-link-entidade"
                                                   href={`/sync/${idDirecao}/${encodeURIComponent(tenant)}/${e.entidade}`
                                                         + (dados.corte ? `?corte=${encodeURIComponent(dados.corte)}` : '')}
                                                   title="Escolher item a item">{e.label} →</a>
                                            )}
                                        </td>
                                        <td className="num">{e.origem ?? '—'}</td>
                                        <td className="num">{e.destino ?? '—'}</td>
                                        <td className="num">
                                            {e.delta === null ? '—'
                                                : (e.delta === 0 ? '=' : (e.delta > 0 ? `+${e.delta}` : e.delta))}
                                        </td>
                                        <td>
                                            {e.indisponivel ? (
                                                <span className="sync-badge indisp"
                                                      title={e.indisponivel}>indisponível</span>
                                            ) : e.executavel === false ? (
                                                <span className="sync-badge warn"
                                                      title={e.motivo_leitura
                                                          || 'A comparação já funciona; a aplicação depende de um caminho de escrita próprio'}>
                                                    {e.iguais ? 'idêntico'
                                                        : (e.delta === 0 ? 'conteúdo diferente' : 'diferente')}
                                                    {' · só comparação'}
                                                </span>
                                            ) : e.iguais ? (
                                                <span className="sync-badge igual">idêntico</span>
                                            ) : (
                                                <span className="sync-badge dif">
                                                    {e.delta === 0
                                                        ? 'mesma quantidade, conteúdo diferente'
                                                        : 'diferente'}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {comDiferenca.some((e) => e.delta === 0) && (
                        <p className="sync-nota destaque">
                            Repare nas linhas com <strong>Δ =</strong> e conteúdo diferente: a
                            quantidade bate nos dois ambientes, mas o conteúdo não. É por isso que a
                            comparação não pode ser só de números.
                        </p>
                    )}

                    <p className="sync-nota">
                        Clique numa entidade com diferença para escolher item a item. O corte
                        fixado aqui viaja no link, para a seleção falar do mesmo instante.
                    </p>
                    {(dados.entidades || []).filter((e) => e.executavel === false).map((e) => (
                        <p className="sync-nota destaque" key={e.entidade}>
                            <strong>{e.label}</strong> é <strong>só comparação</strong> e não abre
                            para seleção — para você não montar um plano que não pode ser
                            executado. {e.motivo_leitura}
                        </p>
                    ))}
                </>
            )}
        </div>
    );
}
